import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/UI/Header';
import SearchBar from './components/SearchBar';
import ProjectFilters from './components/ProjectFilters';
import ProjectGrid from './components/ProjectGrid';
import Footer from '../../components/UI/Footer';
import WaveDivider from '../home-landing/components/WaveDivider';
import Icon from '../../components/AppIcon';
import Button from '../../components/UI/Button';
import { Project, FilterState, ProjectCategory, ProjectType, ProjectStatus, ShowcaseContent } from './types';
import { useProjects } from '../../hooks/useProjects';
import { useContent } from '../../hooks/useContent';
import { defaultShowcaseContent } from './constants/defaults';
import { motion, AnimatePresence } from 'framer-motion';

const ProjectsShowcase = () => {
  const navigate = useNavigate();
  const { projects: dbProjects, loading: projectsLoading } = useProjects();
  const { content: showcaseContent } = useContent<ShowcaseContent>('projects_showcase', defaultShowcaseContent);

  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    technologies: [],
    types: [],
    searchQuery: ''
  });

  // Map DB projects to UI project type
  const projects = useMemo(() => {
    if (!dbProjects) return [];
    return dbProjects.map(p => {
      const details = p.details || {};
      const gallery = Array.isArray(details.gallery) ? details.gallery : [];
      const challenges = Array.isArray(details.challenges) ? details.challenges : [];

      return {
        id: p.id,
        title: p.title || 'Untitled Project',
        description: details.overview?.challenge || p.description || '',
        shortDescription: p.description || '',
        image: p.image_url || 'https://images.unsplash.com/photo-1586965142106-83751d8f57a3',
        alt: p.title || 'Project image',
        technologies: Array.isArray(p.tags) ? p.tags : [],
        categories: (Array.isArray(details.categories) ? details.categories : (details.category ? [details.category] : ['web-development'])) as ProjectCategory[],
        type: (details.type || 'personal') as ProjectType,
        status: (details.status || 'completed') as ProjectStatus,
        demoUrl: p.live_link || '',
        githubUrl: p.github_link || '',
        featured: !!p.is_featured,
        completedDate: details.completionDate || p.created_at || new Date().toISOString(),
        challenges: challenges.map((c: any) => typeof c === 'object' ? c?.title : c).filter(Boolean),
        solutions: challenges.map((c: any) => typeof c === 'object' ? c?.solution : null).filter(Boolean),
        lessonsLearned: [],
        demoCredentials: details.demoCredentials,
        screenshots: gallery.map((img: any, idx: number) => ({
          id: img.id || `img-${idx}`,
          url: img.src || img.url || '',
          alt: img.alt || img.title || 'Screenshot',
          caption: img.title || ''
        }))
      };
    });
  }, [dbProjects]);

  // Get all unique technologies for filter options
  const availableTechnologies = useMemo(() => {
    const techSet = new Set<string>();
    projects.forEach((project) => {
      project.technologies.forEach((tech) => techSet.add(tech));
    });
    return Array.from(techSet).sort();
  }, [projects]);

  // Get search suggestions
  const searchSuggestions = useMemo(() => {
    const suggestions = new Set<string>();
    projects.forEach((project) => {
      suggestions.add(project.title);
      project.technologies.forEach((tech) => suggestions.add(tech));
      project.categories.forEach((cat) => suggestions.add(cat.replace('-', ' ')));
      suggestions.add(project.type || '');
    });
    return Array.from(suggestions).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
  }, [projects]);

  // Filter projects based on current filters
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      if (filters.categories.length > 0) {
        // Project matches if at least one of its categories is in the filter list
        // Note: Filter state currently stores labels because boutons pass labels
        const hasMatchingCategory = project.categories.some(cat => filters.categories.includes(cat));
        if (!hasMatchingCategory) return false;
      }
      if (filters.types.length > 0 && !filters.types.includes(project.type)) return false;
      if (filters.technologies.length > 0) {
        const hasMatchingTech = filters.technologies.some((tech) => project.technologies.includes(tech));
        if (!hasMatchingTech) return false;
      }
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const searchableText = [
          project.title,
          project.description,
          project.shortDescription,
          ...project.technologies,
          ...project.categories.map(c => c.replace('-', ' ')),
          project.type
        ].join(' ').toLowerCase();
        if (!searchableText.includes(query)) return false;
      }
      return true;
    });
  }, [projects, filters]);

  if (projectsLoading) {
    return (
      <div className="min-h-screen bg-[#FFF9EB]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0C2C39]/10 border-t-[#0D9488] rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-[#0C2C39]/40 font-black  tracking-widest text-xs">Parsing Repository Content</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleProjectSelect = (project: Project) => {
    navigate(`/project-details?id=${project.id}`);
  };

  const handleSearchChange = (query: string) => {
    setFilters((prev) => ({ ...prev, searchQuery: query }));
  };

  return (
    <div className="min-h-screen bg-[#FFF9EB]">
      <Header />

      <main className="w-full">
        {/* Dark Header Section mimicking screenshot */}
        <section className="bg-[#0C2C39] pt-48 pb-12 relative">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-inter text-3xl md:text-4xl font-black text-white mb-8 tracking-tighter leading-[0.8] "
            >
              {showcaseContent?.showcaseTitle || "My Projects"}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-inter text-sm md:text-base text-white/70 max-w-4xl mx-auto font-medium leading-relaxed mb-16"
            >
              {showcaseContent?.showcaseSubtitle || "Portfolio Showcase"}
            </motion.p>
          </div>

          {/* Wave Divider at bottom of dark section - transitions to cream background below */}
          <div className="absolute bottom-0 left-0 w-full z-10">
            <WaveDivider color="#FFF9EB" className="h-16 md:h-24" />
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24 relative z-0">
          {/* Search bar moved below wave divider, floating naturally */}
          <div className="flex justify-center mb-16 px-4">
            <div className="w-full max-w-2xl bg-white border-2 border-[#0C2C39] rounded-2xl p-2 shadow-[4px_4px_0px_0px_#0C2C39] flex items-center">
              <div className="flex-1">
                <SearchBar
                          searchQuery={filters.searchQuery}
                          onSearchChange={handleSearchChange}
                          suggestions={searchSuggestions}
                        />
                      </div>
                    </div>
          </div>

          {/* Filters and Grid */}
          <section className="relative">
            <ProjectFilters
              filters={filters}
              onFiltersChange={setFilters}
              availableTechnologies={availableTechnologies}
              categories={showcaseContent?.showcaseCategories}
            />

            <div className="flex items-center justify-between mb-12 mt-16 px-4">
              <div>
                <h2 className="text-2xl font-black text-[#0C2C39]  tracking-tighter">
                  Filtered Results
                </h2>
                <p className="text-[10px] font-black text-[#0C2C39]/40  tracking-widest mt-1">
                  Showing {filteredProjects.length} matching architectures
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-[#0C2C39]/60  tracking-widest">
                  Displaying All
                </span>
                <div className="w-8 h-[2px] bg-[#0D9488]" />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ProjectGrid
                  projects={filteredProjects}
                  onProjectSelect={handleProjectSelect}
                />
              </motion.div>
            </AnimatePresence>

            {filteredProjects.length === 0 && (
              <div className="text-center py-32 bg-white border-2 border-[#0C2C39] border-dashed rounded-[2.5rem] p-12">
                <Icon name="SearchX" size={64} className="text-[#0C2C39]/20 mx-auto mb-6" />
                <h3 className="text-3xl font-black text-[#0C2C39]  tracking-tighter mb-4">No Matches Identified</h3>
                <p className="text-[#0C2C39]/60 max-w-sm mx-auto font-medium mb-8">
                  Your current filters didn't return any results. System recommends broadening the search parameters.
                </p>
                <button
                  onClick={() => setFilters({ categories: [], technologies: [], types: [], searchQuery: '' })}
                  className="bg-[#0C2C39] text-white px-8 py-4 rounded-xl font-black  tracking-widest text-xs transition-all hover:bg-[#0D9488]"
                >
                  Reset Search
                </button>
              </div>
            )}
          </section>

          {/* Bottom CTA */}
          <section className="mt-48 bg-[#0C2C39] rounded-[3rem] p-16 relative overflow-hidden group shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/20 to-transparent pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 bg-white/5 w-64 h-64 rounded-full blur-3xl group-hover:bg-[#0D9488]/10 transition-colors duration-700" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter leading-tight  mb-6">
                  Ready to Build <br />
                  Something <span className="text-[#0D9488]">Exceptional?</span>
                </h2>
                <p className="text-slate-400 text-lg font-medium mb-10 max-w-md italic leading-relaxed">
                  "Code is the medium of creation in the digital age. Let's sculpt something that matters."
                </p>
                <div className="flex flex-wrap gap-6">
                  <Button
                    className="bg-[#0D9488] text-white hover:bg-white hover:text-[#0C2C39] px-10 py-5 rounded-2xl font-black  tracking-widest text-sm shadow-xl shadow-[#0D9488]/20 transition-all duration-300"
                    onClick={() => navigate('/contact-form')}
                  >
                    Initiate Collaboration
                    <Icon name="ArrowRight" size={20} />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 opacity-40">
                <div className="p-8 border-2 border-white/10 rounded-3xl group-hover:border-[#0D9488]/30 transition-colors">
                  <Icon name="Cpu" size={32} className="text-[#0D9488] mb-4" />
                  <p className="text-[10px] font-black text-white  tracking-widest mb-1">Scale</p>
                  <p className="text-white/60 text-xs font-bold leading-relaxed">Architecting for high-performance and future growth.</p>
                </div>
                <div className="p-8 border-2 border-white/10 rounded-3xl mt-12 group-hover:border-[#0D9488]/30 transition-colors">
                  <Icon name="Fingerprint" size={32} className="text-[#0D9488] mb-4" />
                  <p className="text-[10px] font-black text-white  tracking-widest mb-1">Detail</p>
                  <p className="text-white/60 text-xs font-bold leading-relaxed">Precision-focused UI and bulletproof code logic.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectsShowcase;